import { apiClient } from "@api/client";
import { request } from "@api/request";
import type { components } from "@api/schema";

/** Stage lifecycle, mirrored from the backend `StageStatus`. */
export type StageStatus = components["schemas"]["StageStatus"];

export interface VisitStage {
  id: string;
  position: number;
  name: string;
  status: StageStatus;
  enteredAt: string | null;
  completedAt: string | null;
}

/**
 * A visit on the "today" view, joined to the human identifiers and current
 * stage the patient-flow rail renders. One pet per visit; `currentStage` is
 * where the visit is "at" (or null if it has no stages yet).
 */
export interface TodayVisit {
  id: string;
  patientId: string;
  patientName: string;
  clientId: string | null;
  clientName: string | null;
  assignedUserId: string | null;
  assignedUserName: string | null;
  currentRoomUuid: string | null;
  visitType: string;
  reason: string | null;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  /** When the visit was cancelled (BE-1956), or null if it wasn't. A cancelled
   * visit keeps its record and stays on the today view; cancel is only allowed
   * before the visit is underway, so a cancelled visit has no active/done stage
   * (`currentStage` is null). */
  cancelledAt: string | null;
  /** Row creation time — one half of the history keyset cursor (BE-2116),
   * and the sort fallback for unscheduled visits. Absent when the backend
   * predates BE-2116 (the field is additive). */
  createdAt?: string;
  currentStage: VisitStage | null;
}

/** Keyset cursor for visit history — the last row's (createdAt, id) pair.
 * The backend 422s on half a cursor, so both travel together. */
export interface VisitHistoryCursor {
  before: string;
  beforeUuid: string;
}

// Pure status predicates live in visitStatus.ts (no API-client import chain,
// so pure helpers/tests can use them); re-exported here as the public home.
export {
  findBoardVisit,
  isOnTodayBoard,
  isVisitCancelled,
  isVisitComplete,
  isVisitLate,
  isVisitSettled,
} from "./visitStatus";

export const toVisitStage = (
  s: components["schemas"]["VisitStageOut"],
): VisitStage => ({
  id: s.uuid,
  position: s.position,
  name: s.name,
  status: s.status,
  enteredAt: s.entered_at,
  completedAt: s.completed_at,
});

/**
 * A single visit with its full ordered list of stages, from
 * `GET /visits/{uuid}`. The flow tracker renders these once a
 * patient-flow card is expanded; the collapsed card only needs `currentStage`
 * off the lighter `/visits/today` payload.
 */
export interface VisitDetail {
  id: string;
  patientId: string;
  visitType: string;
  reason: string | null;
  scheduledStart: string | null;
  scheduledEnd: string | null;
  stages: VisitStage[];
}

export const toVisitDetail = (
  v: components["schemas"]["VisitDetailOut"],
): VisitDetail => ({
  id: v.uuid,
  patientId: v.patient_uuid,
  visitType: v.visit_type,
  reason: v.reason,
  scheduledStart: v.scheduled_start,
  scheduledEnd: v.scheduled_end,
  // The backend returns stages already ordered by position; sort defensively
  // so the tracker never depends on response ordering.
  stages: [...v.stages]
    .sort((a, b) => a.position - b.position)
    .map(toVisitStage),
});

export const toTodayVisit = (
  v: components["schemas"]["TodayVisitOut"],
): TodayVisit => ({
  id: v.uuid,
  patientId: v.patient_uuid,
  patientName: v.patient_name,
  clientId: v.client_uuid,
  clientName: v.client_name,
  assignedUserId: v.assigned_user_uuid,
  assignedUserName: v.assigned_user_name,
  currentRoomUuid: v.current_room_uuid,
  visitType: v.visit_type,
  reason: v.reason,
  scheduledStart: v.scheduled_start,
  scheduledEnd: v.scheduled_end,
  cancelledAt: v.cancelled_at,
  createdAt: v.created_at,
  currentStage: v.current_stage ? toVisitStage(v.current_stage) : null,
});

/** Page size for the rail's "Recent visits" view (see forPatient). */
export const VISIT_HISTORY_LIMIT = 50;

export const visitsClient = {
  /** Today's visits, with patient/owner/assignee names and current stage. */
  today: (): Promise<TodayVisit[]> =>
    request(() => apiClient.GET("/visits/today")).then((rows) =>
      rows.map(toTodayVisit),
    ),

  /**
   * Visits from earlier days that were started but never finished, most recent
   * first. The backend owns the rule and the lookback (brian's
   * `VisitService.unfinished_before_today`, BE-2198); `/visits/today` is windowed
   * to the clinic's day and never returns these.
   */
  open: (): Promise<TodayVisit[]> =>
    request(() => apiClient.GET("/visits/open")).then((rows) =>
      rows.map(toTodayVisit),
    ),

  /** A single visit with its full ordered stages, for the flow tracker. */
  get: (uuid: string): Promise<VisitDetail> =>
    request(() =>
      apiClient.GET("/visits/{visit_uuid}", {
        params: { path: { visit_uuid: uuid } },
      }),
    ).then(toVisitDetail),

  /**
   * One page of a patient's visits, newest first — the rail's "Recent
   * visits" view. Same today-view shape (patient/owner/assignee names,
   * current stage), reused rather than a bespoke history type. Older pages
   * via the composite keyset cursor (BE-2116).
   */
  forPatient: (
    patientUuid: string,
    cursor?: VisitHistoryCursor,
  ): Promise<TodayVisit[]> =>
    request(() =>
      apiClient.GET("/patients/{patient_uuid}/visits", {
        params: {
          path: { patient_uuid: patientUuid },
          query: {
            limit: VISIT_HISTORY_LIMIT,
            before: cursor?.before,
            before_uuid: cursor?.beforeUuid,
          },
        },
      }),
    ).then((rows) => rows.map(toTodayVisit)),
};
